import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Chip,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  alpha,
} from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import EditTextDialog from "./EditTextDialog";
import { marked } from "marked";
import ExportButton from "./ExportButton";
import { handlePrint } from "./functions";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { useSnackbar } from "./SnackbarHandler";

const EssayList = ({
  essays: initialEssays,
  statusFilter = "",
  onEssaysDelete,
  onEssayUpdate,
  theme,
  styles,
}) => {
  const [essays, setEssays] = useState(initialEssays);

  const [selectedEssays, setSelectedEssays] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEssay, setSelectedEssay] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Add this useEffect to update when props change
  useEffect(() => {
    setEssays(initialEssays);
  }, [initialEssays]);

  // Add real-time listener for queued essays
  // useEffect(() => {
  //   // Start with initial essays
  //   setEssays(initialEssays);

  //   // Find queued essays to listen for updates
  //   const queuedEssays = initialEssays.filter((essay) => essay.queued);

  //   // If no queued essays, we don't need listeners
  //   if (queuedEssays.length === 0) return;

  //   // Set up listeners for each queued essay
  //   const unsubscribes = queuedEssays.map((essay) => {
  //     const essayRef = doc(db, "essays", essay.id);

  //     return onSnapshot(
  //       essayRef,
  //       (docSnapshot) => {
  //         if (docSnapshot.exists()) {
  //           const updatedEssay = {
  //             id: docSnapshot.id,
  //             ...docSnapshot.data(),
  //           };

  //           // Update the essay in our local state
  //           setEssays((prevEssays) =>
  //             prevEssays.map((prevEssay) =>
  //               prevEssay.id === updatedEssay.id ? updatedEssay : prevEssay,
  //             ),
  //           );
  //         }
  //       },
  //       (error) => {
  //         console.error("Error listening for essay updates:", error);
  //       },
  //     );
  //   });

  //   // Clean up listeners when component unmounts
  //   return () => {
  //     unsubscribes.forEach((unsubscribe) => unsubscribe());
  //   };
  // }, [initialEssays]);

  const handleSelectEssay = (event, essay) => {
    setSelectedEssays((prev) =>
      event.target.checked
        ? [...prev, essay]
        : prev.filter((e) => e.id !== essay.id),
    );
  };

  const handleMenuClick = (e, essay) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedEssay(essay);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEssay(null);
  };

  const handleCopy = async (essayResults) => {
    const plainText = essayResults;
    const htmlContent = marked.parse(plainText);

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plainText], { type: "text/plain" }),
          "text/html": new Blob([htmlContent], { type: "text/html" }),
        }),
      ]);
      showSnackbar("Kopierad till urklipp!", "success");
    } catch (err) {
      console.error("Fel vid formaterad kopiering: ", err);
      try {
        await navigator.clipboard.writeText(plainText);
        showSnackbar("Kopierad som vanlig text!", "success");
      } catch (fallbackErr) {
        showSnackbar("Kunde inte kopiera texten", "error");
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedEssay) return;
    try {
      await onEssaysDelete([selectedEssay]);
    } catch (error) {
      console.error("Error deleting essay: ", error);
    }
    handleMenuClose();
  };

  const handleDeleteMultiple = async () => {
    try {
      await onEssaysDelete(selectedEssays);
      setSelectedEssays([]);
    } catch (error) {
      console.error("Error deleting essays: ", error);
    }
  };

  const getFilteredEssays = () => {
    switch (statusFilter) {
      case "queued":
        return essays.filter((essay) => essay.queued);
      case "ready":
        return essays.filter((essay) => !essay.reviewed && !essay.queued);
      case "reviewed":
        return essays.filter((essay) => essay.reviewed);
      default:
        return essays;
    }
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      A: "#2E7D32",
      B: "#1976D2",
      C: "#FFA000",
      D: "#ED6C02",
      E: "#D32F2F",
      F: "#757575",
    };
    return gradeColors[grade] || "#757575";
  };

  return (
    <>
      {selectedEssays.length > 0 && (
        <Box sx={{ mb: 4, textAlign: "left" }}>
          <ExportButton
            content={selectedEssays.map((essay) => ({
              content: essay.results,
              fileName: essay.fileName,
            }))}
            styles={styles}
            bigButton
          />
          <Button
            variant="print"
            onClick={() => {
              if (
                window.confirm(
                  `Är du säker på att du vill radera ${selectedEssays.length} ${selectedEssays.length === 1 ? "bedömning" : "bedömningar"}?`,
                )
              ) {
                handleDeleteMultiple();
              }
            }}
            startIcon={<DeleteIcon />}
            sx={{ ml: 3 }}
          >
            Radera {selectedEssays.length}{" "}
            {selectedEssays.length === 1 ? "bedömning" : "bedömningar"}
          </Button>
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          border:
            theme.palette.mode === "dark"
              ? "1px solid #35414d"
              : "1px solid #eee",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    getFilteredEssays().length > 0 &&
                    selectedEssays.length === getFilteredEssays().length
                  }
                  indeterminate={
                    selectedEssays.length > 0 &&
                    selectedEssays.length < getFilteredEssays().length
                  }
                  onChange={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setSelectedEssays(
                      event.target.checked ? getFilteredEssays() : [],
                    );
                  }}
                />
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Namn</TableCell>
              <TableCell>Klass</TableCell>
              <TableCell>Bedömningsmatris</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Betyg</TableCell>
              <TableCell align="right">Hantera</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredEssays().map((essay) => (
              <TableRow
                key={essay.id}
                hover
                component={Link}
                to={`/app/uppsats/${essay.id}`}
                sx={{
                  textDecoration: "none",
                  "&:last-child td, &:last-child th": { border: 0 },
                  ...(essay.queued && {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.warning.main, 0.1) // Dark mode: slightly transparent warning color
                        : alpha(theme.palette.warning.main, 0.05), // Light mode: very light warning color
                  }),
                }}
              >
                <TableCell
                  padding="checkbox"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedEssays.some((e) => e.id === essay.id)}
                    onChange={(event) => handleSelectEssay(event, essay)}
                  />
                </TableCell>
                <TableCell sx={{ width: "190px" }}>
                  {essay.queued ? (
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pointerEvents: "auto !important",
                          }}
                        >
                          <CircularProgress
                            size={14}
                            thickness={5}
                            sx={{ color: "inherit" }}
                          />
                          I kö
                        </Box>
                      }
                      size="small"
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "#1976d2"
                            : theme.palette.warning.main,
                        color: theme.palette.mode === "dark" ? "#fff" : "#fff",
                        fontWeight: 500,
                        height: "24px",
                        cursor: "pointer",
                        pointerEvents: "auto !important",
                        ml: 0,
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.main} !important`,
                          color: "white !important",
                        },
                      }}
                    />
                  ) : essay.reviewed ? (
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pointerEvents: "auto !important",
                          }}
                        >
                          <TaskAltIcon sx={{ fontSize: 14 }} />
                          Granskad
                        </Box>
                      }
                      size="small"
                      sx={{
                        ...styles.greenChip,
                        height: "24px",
                        cursor: "pointer",
                        pointerEvents: "auto !important",
                        ml: 0,
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.main} !important`,
                          color: "white !important",
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pointerEvents: "auto !important",
                          }}
                        >
                          <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />
                          Ej granskad
                        </Box>
                      }
                      size="small"
                      sx={{
                        ...styles.grayChip,
                        height: "24px",
                        cursor: "pointer",
                        pointerEvents: "auto !important",
                        ml: 0,
                        "&:hover": {
                          backgroundColor: `${theme.palette.primary.main} !important`,
                          color: "white !important",
                        },
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>{essay.fileName}</TableCell>
                <TableCell>{essay.className}</TableCell>
                <TableCell>{essay.rubricName}</TableCell>
                <TableCell>
                  {essay.createdAt.toDate().toLocaleDateString()}
                </TableCell>

                <TableCell>
                  {essay.queued ? (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "transparent",
                      }}
                    >
                      <CircularProgress
                        size={18}
                        thickness={5}
                        sx={{ color: theme.palette.warning.main }}
                      />
                    </Box>
                  ) : essay.scores ? (
                    <Box
                      sx={{
                        bgcolor: getGradeColor(essay.scores.overallScore.grade),
                        color: "white",
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {essay.scores.overallScore.grade}
                      </Typography>
                    </Box>
                  ) : null}
                </TableCell>

                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, essay)}
                    size="small"
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
            width: "auto",
          },
        }}
      >
        <MenuItem
          onClick={() => setEditDialogOpen(true)}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Redigera namn på text
        </MenuItem>

        <MenuItem
          onClick={() => handleCopy(selectedEssay?.results)}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ContentCopyIcon sx={{ mr: 1 }} />
          Kopiera
        </MenuItem>
        <MenuItem
          onClick={() => handlePrint(selectedEssay?.results)}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <PrintIcon sx={{ mr: 1 }} />
          Skriv ut
        </MenuItem>
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ExportButton
            content={selectedEssay?.results}
            styles={styles}
            isMenuItem={true}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleDelete}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
            color: "rgb(226 90 90)",
          }}
        >
          <DeleteIcon sx={{ mr: 1, color: "rgb(226 90 90)" }} />
          Radera
        </MenuItem>
      </Menu>

      <EditTextDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          handleMenuClose();
        }}
        essay={selectedEssay}
        onUpdate={onEssayUpdate}
      />
    </>
  );
};

export default EssayList;
